<template>
  <permission-gate
    v-slot="{ canEdit }"
    moduleKey="orden-produccion"
  >
    <main-breadcrumb
      :items="items"
      showBackButton
    ></main-breadcrumb>
    <main-buttons-bar :align="'space-between'">
      <template #left>
        <custom-title>Listado de ordenes de producción</custom-title>
      </template>
      <template #right>
        <el-tooltip
          :disabled="canEdit"
          content="El usuario no posee permisos de edición"
          placement="top"
        >
          <el-dropdown
            v-if="mostrarGestionPedidos"
            class="crear-orden-dropdown"
            popper-class="popover-crear-orden-dropdown"
            :disabled="!canEdit"
          >
            <el-button
              type="primary"
              size="default"
              :icon="IconAdd"
            >
              Nueva orden de producción<el-icon class="el-icon--right"><IconChevronDown /></el-icon>
            </el-button>
            <template #dropdown>
              <el-dropdown-menu size="default">
                <el-dropdown-item @click="() => (modalVisible = true)"
                  >Crear sin pedido</el-dropdown-item
                >
                <el-dropdown-item @click="() => (modalPedidosVisible = true)"
                  >Crear desde Pedido</el-dropdown-item
                >
              </el-dropdown-menu>
            </template>
          </el-dropdown>

          <el-button
            v-else
            type="primary"
            size="default"
            :icon="IconAdd"
            :disabled="!canEdit"
            class="create-orden-button"
            @click="() => (modalVisible = true)"
            >Nueva orden de producción</el-button
          >
        </el-tooltip>

        <modal-select-product-type
          v-model="modalVisible"
          title="Nueva orden de producción"
          url="/page/work-orders/edit"
          @close-modal="() => (modalVisible = false)"
        />
      </template>
    </main-buttons-bar>
    <work-order-filters />
    <custom-table
      :requestApi="findAllOrdenesProduccionHTTP"
      :pathEdit="'/page/work-orders/edit'"
      :paramId="'Codigo'"
      :data="ordenesProduccion"
      :columns="columns"
      :showDetailButton="false"
      :requestArgs="filters"
      :fixedActions="'right'"
    >
      <template #custom-actions="{ row }">
        <el-tooltip
          :content="!canEdit ? 'El usuario no posee permisos de edición' : 'Duplicar OP'"
          placement="bottom"
        >
          <span>
            <el-button
              :icon="IconCopy"
              size="small"
              :disabled="!canEdit"
              @click.prevent="handleOpenDuplicateModal(row)"
            />
          </span>
        </el-tooltip>
      </template>
    </custom-table>

    <modal-duplicate-work-order
      v-if="modalDuplicateVisible"
      v-model="modalDuplicateVisible"
      :codigoOT="duplicateCodigoOT"
      :codigoUnidadVenta="selectedRowForDuplication?.CodigoUnidadVenta ?? null"
      :isObsoleta="selectedRowForDuplication?.isDesactualizado"
      @confirm="confirmDuplicate"
    />
  </permission-gate>
</template>

<script setup>
import {
  ref,
  onBeforeMount,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useWorkOrderStore } from '../stores/work_order_store.js';
import { useElementoTipoStore } from '@/stores/elemento_tipo/elemento_tipo_store.js';
import MainBreadcrumb from '@/shared/layout/main/main_breadcrum.vue';
import MainButtonsBar from '@/shared/layout/main/buttons_bar/main_buttons_bar.vue';
import CustomTable from '@/shared/components/custom_table.vue';
import CustomTitle from '@/shared/components/custom_title.vue';
import IconAdd from '~icons/mdi/add';
import IconCopy from '~icons/mdi/content-copy';
import IconChevronDown from '~icons/mdi/chevron-down';
import ModalSelectProductType from '@/shared/components/modal_select_product_type.vue';
import WorkOrderFilters from './work_order_filters.vue';
import { useConfiguracionStore } from '@/stores/configuracion-general.js';
import PermissionGate from '@/shared/components/permission_gate.vue';
import ModalDuplicateWorkOrder from '@/shared/components/modal_duplicate_work_order.vue';
import { useLoading } from '@/shared/composables/loading.js';
import { useMessageSuccess } from '@/shared/composables/message_success.js';
import { useMessageError } from '@/shared/composables/message_error.js';

const elementoTipoStore = useElementoTipoStore();
const workOrderStore = useWorkOrderStore();
const configuracionStore = useConfiguracionStore();

const { ordenesProduccion, filters, modalPedidosVisible, modalPedidosAOpVisible } =
  storeToRefs(workOrderStore);
const {
  firstRequest,
  resetFilter,
  resetOrdenProduccion,
  findAllOrdenesProduccionHTTP,
  cloneOrdenProduccionHTTP,
} = workOrderStore;
const { mostrarGestionPedidos } = storeToRefs(configuracionStore);

const modalVisible = ref(false);
const modalDuplicateVisible = ref(false);
const duplicateCodigoOT = ref(null);
const selectedRowForDuplication = ref(null);
// local state
const items = [
  { label: 'Inicio', to: '/page/home', replace: false },
  { label: 'Listado', to: null, replace: false },
];

const columns = [
  { field: 'Codigo', label: 'ID', width: 100, sortable: true, isVisible: true },
  {
    field: 'FechaCreacion',
    label: 'Fecha solicitud',
    width: 120,
    sortable: true,
    isVisible: true,
    isDate: true,
  },
  { field: 'Cliente', label: 'Cliente', width: 280, sortable: true, isVisible: true },
  { field: 'Producto', label: 'Producto', width: 250, sortable: true, isVisible: true },
  {
    field: 'Cantidad',
    label: 'Cantidad',
    width: 140,
    sortable: true,
    isVisible: true,
    isNumber: true,
    precision: 0,
  },
  { field: 'UnidadVenta', label: 'Unidad Venta', width: 130, sortable: true, isVisible: true },
  {
    field: 'CodigosEquivalentes',
    label: 'Código Equivalente',
    width: 180,
    sortable: true,
    isVisible: true,
  },
  {
    field: 'CodigosAdicionales',
    label: 'Código adicional',
    width: 180,
    sortable: true,
    isVisible: true,
  },
  { field: 'Estado', label: 'Estado', width: 120, sortable: true, isVisible: true },
  {
    field: 'FechaEstado',
    label: 'Fecha estado',
    width: 120,
    sortable: true,
    isVisible: true,
    isDate: true,
  },
];

const handleOpenDuplicateModal = (row) => {
  selectedRowForDuplication.value = row;
  duplicateCodigoOT.value = row?.Codigo ?? null;
  modalDuplicateVisible.value = !!duplicateCodigoOT.value;
};

const confirmDuplicate = async ({ codigoOT, cantidad, actualizarET }) => {
  try {
    useLoading().openLoading();

    const nuevaOT = await cloneOrdenProduccionHTTP(codigoOT, cantidad, actualizarET);

    useMessageSuccess().open('La orden de producción se ha copiado correctamente.');

    modalDuplicateVisible.value = false;
    duplicateCodigoOT.value = null;

    await firstRequest();
  } catch (err) {
    console.error(err);
    useMessageError().open('Ha ocurrido un error al intentar copiar la orden de producción.');
  } finally {
    useLoading().closeLoading();
  }
};

onBeforeMount(async () => {
  resetFilter();
  resetOrdenProduccion();
  firstRequest();
  elementoTipoStore.resetElementoTipoList();
});
</script>

<style>
.crear-orden-dropdown.is-disabled,
.crear-orden-dropdown.is-disabled > button,
.create-orden-button.is-disabled {
  cursor: not-allowed !important;
}

.popover-crear-orden-dropdown {
  min-width: 240px !important;
  box-shadow: 2px 2px 5px 0px rgba(0, 0, 0, 0.5) !important;
}

.popover-crear-orden-dropdown .el-dropdown-menu__item {
  padding: 8px 20px !important;
  font-size: 14px !important;
  line-height: 1.3 !important;
  white-space: nowrap !important;
}
.popover-crear-orden-dropdown .el-dropdown-menu__item:hover {
  background-color: var(--el-color-primary) !important;
  color: var(--el-color-white) !important;
}
</style>
